<template>
  <section class="practice-page">
    <header class="toolbar-card">
      <button type="button" class="back-btn" @click="goBack">{{ t("back") }}</button>
      <div>
        <p class="panel-tag">{{ t("eyebrow") }}</p>
        <h1 class="page-title">{{ t("title") }}</h1>
      </div>
    </header>

    <div v-if="loading" class="state-card">{{ t("loading") }}</div>
    <div v-else-if="!current" class="state-card">{{ t("noData") }}</div>
    <template v-else>
      <article class="question-card">
        <div class="progress-panel">
          <div class="progress-copy">
            <strong>{{ t("progress") }}</strong>
            <span>{{ currentIndex + 1 }} / {{ questions.length }}</span>
          </div>
          <div class="progress-track" aria-hidden="true">
            <div class="progress-fill" :style="{ width: `${progressPercent}%` }"></div>
          </div>
        </div>

        <div class="question-head">
          <p class="question-kicker">{{ t("questionNo") }} {{ currentIndex + 1 }}</p>
          <div class="word-audio">
            <strong>{{ current.wordText }}</strong>
            <span>{{ current.wordPhonetic }}</span>
            <button type="button" class="speak-btn" @click="speakEnglish(current.wordText)">
              {{ t("speak") }}
            </button>
          </div>
        </div>

        <p class="question-body">{{ t("questionBody", { word: current.wordText }) }}</p>

        <table class="typing-table">
          <thead>
            <tr>
              <th>{{ t("item") }}</th>
              <th v-for="part in current.parts" :key="part.index">{{ part.label }}</th>
              <th>{{ t("word") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ t("meaning") }}</td>
              <td v-for="part in current.parts" :key="`m-${part.index}`">{{ part.meaning }}</td>
              <td>{{ current.wordMeaning }}</td>
            </tr>
            <tr>
              <td>{{ t("typing") }}</td>
              <td v-for="(part, index) in current.parts" :key="`i-${part.index}`">
                <input v-model.trim="answers[index]" type="text" :placeholder="part.label" />
              </td>
              <td>
                <input v-model.trim="wordAnswer" type="text" :placeholder="t('word')" />
              </td>
            </tr>
          </tbody>
        </table>

        <div class="action-row">
          <button type="button" class="action-btn" @click="checkTyping">{{ t("confirm") }}</button>
        </div>

        <div class="result-box">
          <p v-if="resultState === 'ok'" class="ok">{{ t("correct") }}</p>
          <template v-else-if="resultState === 'ng'">
            <p class="ng">{{ t("wrongGuide") }}</p>
            <p class="answer-summary">{{ answerSummary }}</p>
          </template>
        </div>
      </article>

      <div class="pager">
        <button type="button" @click="move(-1)" :disabled="currentIndex <= 0">{{ t("prev") }}</button>
        <button type="button" @click="move(1)" :disabled="currentIndex >= questions.length - 1">{{ t("next") }}</button>
      </div>
    </template>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import {
  API_BASE,
  ensureApiArray,
  getPronunciation,
  getStructureParts,
  getWordMeaning,
  speakEnglish,
} from "./englishStudyShared";

const router = useRouter();
const STORAGE_CONFIG = "english_practice_config";
const STORAGE_INDEX = "english_practice_typing_index";
const { lang } = useSiteLanguage();

const loading = ref(false);
const questions = ref([]);
const currentIndex = ref(0);
const answers = ref([]);
const wordAnswer = ref("");
const resultState = ref("");

const current = computed(() => questions.value[currentIndex.value] || null);
const progressPercent = computed(() => {
  if (!questions.value.length) {
    return 0;
  }
  return ((currentIndex.value + 1) / questions.value.length) * 100;
});

const i18n = {
  ja: {
    back: "Back",
    eyebrow: "TYPING",
    title: "入力式練習",
    loading: "Loading...",
    noData: "練習に使える単語がありません。",
    progress: "進捗",
    questionNo: "Question",
    questionBody: "{word} の構造パーツと単語を、下の意味を見ながら入力してください。",
    speak: "Read",
    item: "項目",
    word: "単語",
    meaning: "意味",
    typing: "入力",
    confirm: "答え合わせ",
    correct: "正解です。",
    wrongGuide: "入力が一致しませんでした。正しい答えを確認してください。",
    prev: "前へ",
    next: "次へ",
  },
  zh: {
    back: "Back",
    eyebrow: "TYPING",
    title: "输入题练习",
    loading: "Loading...",
    noData: "没有可用于练习的单词。",
    progress: "进度",
    questionNo: "题目",
    questionBody: "请根据下面的意义，输入 {word} 的结构部分和完整单词。",
    speak: "朗读",
    item: "项目",
    word: "单词",
    meaning: "意义",
    typing: "输入",
    confirm: "检查答案",
    correct: "回答正确。",
    wrongGuide: "输入不一致。请查看下方的正确答案。",
    prev: "上一题",
    next: "下一题",
  },
  en: {
    back: "Back",
    eyebrow: "TYPING",
    title: "Typing Practice",
    loading: "Loading...",
    noData: "No words available for practice.",
    progress: "Progress",
    questionNo: "Question",
    questionBody: "Type the structure parts and the full word for {word} by using the meanings below.",
    speak: "Read",
    item: "Item",
    word: "Word",
    meaning: "Meaning",
    typing: "Typing",
    confirm: "Check Answer",
    correct: "Correct.",
    wrongGuide: "The input does not match. Review the correct answer below.",
    prev: "Previous",
    next: "Next",
  },
};

function t(key, params = null) {
  const text = i18n[lang.value]?.[key] || key;
  if (!params) {
    return text;
  }

  return text.replace(/\{(\w+)\}/g, (_, token) => String(params[token] ?? ""));
}

const answerSummary = computed(() => {
  if (!current.value) {
    return "";
  }

  const parts = current.value.parts.map((part) => `${part.label}: ${part.text}`).join(" / ");
  return `${parts} / ${t("word")}: ${current.value.wordText}`;
});

function matchRootFilter(word, keyword, selectedRootId) {
  const rootId = String(word.root_id || "").toLowerCase();
  const key = String(keyword || "").trim().toLowerCase();
  const selectedId = String(selectedRootId || "").trim().toLowerCase();
  if (selectedId && rootId === selectedId) {
    return true;
  }

  return Object.values(word.structure || {}).some((item) => String(item || "").toLowerCase() === key);
}

function buildQuestionBank(words) {
  return words
    .map((word) => {
      const parts = getStructureParts(word, lang.value).map((part) => ({
        ...part,
        meaning: lang.value === "zh" ? part.meaningZh : lang.value === "en" ? part.meaningEn : part.meaningJa,
      }));

      return {
        wordText: word.word_text,
        wordPhonetic: getPronunciation(word),
        wordMeaning: getWordMeaning(word, lang.value),
        parts,
      };
    })
    .filter((word) => word.parts.length > 0);
}

function resetInputs() {
  answers.value = current.value ? new Array(current.value.parts.length).fill("") : [];
  wordAnswer.value = "";
  resultState.value = "";
}

function normalize(value) {
  return String(value || "").trim().toLowerCase();
}

function checkTyping() {
  if (!current.value) {
    return;
  }

  const partMatch = current.value.parts.every((part, index) => normalize(part.text) === normalize(answers.value[index]));
  const wordMatch = normalize(current.value.wordText) === normalize(wordAnswer.value);
  resultState.value = partMatch && wordMatch ? "ok" : "ng";
}

function move(step) {
  const next = currentIndex.value + step;
  if (next < 0 || next >= questions.value.length) {
    return;
  }

  currentIndex.value = next;
  localStorage.setItem(STORAGE_INDEX, String(next));
}

async function loadQuestions() {
  loading.value = true;
  try {
    const cfgRaw = localStorage.getItem(STORAGE_CONFIG);
    const cfg = cfgRaw ? JSON.parse(cfgRaw) : {};
    let keyword = cfg.useRootFilter ? String(cfg.rootKeyword || "").trim() : "";
    const selectedRootId = cfg.useRootFilter ? String(cfg.selectedRootId || "").trim() : "";
    if (!keyword) {
      keyword = "act";
    }

    const response = await axios.get(`${API_BASE}/api/word/search`, {
      params: keyword ? { keyword } : {},
      timeout: 10000,
    });
    const wordsRaw = ensureApiArray(response, "Word search API");
    const words = cfg.useRootFilter ? wordsRaw.filter((word) => matchRootFilter(word, keyword, selectedRootId)) : wordsRaw;
    questions.value = buildQuestionBank(words);

    const shouldResume = String(localStorage.getItem("english_practice_resume")) === "true";
    const savedIndex = Number(localStorage.getItem(STORAGE_INDEX) || "0");
    currentIndex.value = shouldResume && Number.isFinite(savedIndex)
      ? Math.max(0, Math.min(savedIndex, Math.max(0, questions.value.length - 1)))
      : 0;
    resetInputs();
  } catch (error) {
    console.error("Failed to load typing practice:", error);
    questions.value = [];
  } finally {
    loading.value = false;
  }
}

function goBack() {
  router.push("/english/practice");
}

watch(currentIndex, resetInputs);
onMounted(loadQuestions);
</script>

<style scoped>
.practice-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    linear-gradient(180deg, #f5f9ff 0%, #eef6fb 100%);
  color: #163047;
}

.toolbar-card,
.question-card,
.state-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.progress-panel {
  margin-bottom: 20px;
  padding: 14px 16px;
  border: 1px solid rgba(53, 100, 142, 0.14);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(233, 243, 255, 0.84), rgba(248, 251, 255, 0.96));
}

.progress-copy {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #234666;
  font-size: 14px;
}

.progress-copy strong {
  font-size: 15px;
  color: #15314c;
}

.progress-track {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(54, 103, 149, 0.12);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2f7bc1 0%, #143d67 100%);
  transition: width 180ms ease;
}

.toolbar-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.page-title {
  margin: 0;
  color: #10253a;
  font-size: clamp(28px, 4vw, 46px);
}

.back-btn,
.speak-btn,
.action-btn,
.pager button {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.back-btn,
.action-btn,
.pager button {
  border: 0;
  border-radius: 14px;
  padding: 12px 16px;
  font-weight: 700;
}

.back-btn {
  background: #dceaf8;
  color: #133251;
}

.state-card,
.question-card {
  margin-top: 18px;
  padding: 22px;
}

.question-kicker {
  margin: 0;
  color: #35648e;
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.question-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: start;
  flex-wrap: wrap;
}

.word-audio {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.speak-btn,
.action-btn {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.question-body {
  margin: 18px 0;
  font-size: 22px;
  color: #10253a;
}

.typing-table {
  width: 100%;
  border-collapse: collapse;
  background: #fbfdff;
}

.typing-table th,
.typing-table td {
  border: 1px solid rgba(43, 90, 132, 0.18);
  padding: 8px 10px;
  text-align: left;
}

.typing-table input {
  width: 100%;
  border: 1px solid rgba(43, 90, 132, 0.18);
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
  box-sizing: border-box;
}

.action-row {
  margin-top: 16px;
}

.result-box {
  margin-top: 18px;
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 22px;
  background: #f8fbff;
  padding: 16px;
}

.ok {
  margin: 0;
  color: #166534;
  font-weight: 700;
}

.ng {
  margin: 0 0 12px;
  color: #a16207;
  font-weight: 700;
}

.answer-summary {
  margin: 0;
  color: #4c6377;
}

.pager {
  margin-top: 18px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.pager button {
  background: #dceaf8;
  color: #133251;
}

.back-btn:hover,
.speak-btn:hover,
.action-btn:hover,
.pager button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 768px) {
  .practice-page {
    padding: 12px;
  }

  .toolbar-card,
  .question-card,
  .state-card {
    padding: 18px;
    border-radius: 22px;
  }

  .typing-table {
    display: block;
    overflow-x: auto;
  }
}
</style>
